import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { RequestType } from '../../enums/request-type.enum';
import { draftName, draftTabFor } from '../../drafts/draft-tabs';
import type { SaveDraftRequest } from '../models/draft';
import type { AttachmentData, RequestDocAttachmentDto } from '../models/requestDocAttachment';
import { DraftsService } from '../services/drafts.service';
import { RequestsService } from '../services/requests.service';
import { SnackbarService } from '../services/snackbar.service';
import { DRAFT_PAYLOAD_VERSION, emptySnapshot, parseSnapshot, serializeSnapshot } from './draft-snapshot';
import type { DraftSnapshot } from './draft-snapshot';

/** How a Save as Draft attempt ended. */
export const DraftSaveOutcome = {
    /** The draft and any new documents were saved. */
    Saved: 'saved',
    /** The draft was saved, but uploading its new documents failed. */
    DocumentsFailed: 'documents-failed',
    /** Nothing was saved. */
    Failed: 'failed'
} as const;
export type DraftSaveOutcome = (typeof DraftSaveOutcome)[keyof typeof DraftSaveOutcome];

/** The values the Drafts list shows for a draft. For a Journal, `matterNo` must be the "from" matter. */
export type DraftSummaryFields = {
    matterNo: number | null;
    matter: string | null;
    clientName: string | null;
    amount: number | null;
    reason: string | null;
};

/** What a request form hands over when the user clicks Save as Draft. */
export type DraftSaveInput = {
    snapshot: DraftSnapshot;
    summary: DraftSummaryFields;
    /** Documents chosen since the last save, not yet uploaded. */
    newFiles: AttachmentData[];
};

/** What the editor reads from a form to tell whether it holds changes that have not been saved. */
export type DraftFormState = {
    snapshot: DraftSnapshot;
    /** How many chosen documents are still waiting to be uploaded. */
    pendingFiles: number;
};

/**
 * Looks after one request form's draft: opening a saved draft, saving it (creating it the first time),
 * its documents, what happens once the draft has been submitted, and whether the form holds changes that
 * have not been saved (so the user can be warned before they leave).
 * Provided by each request form component, so every open form has its own instance.
 */
@Injectable()
export class DraftEditor {
    private readonly draftsService = inject(DraftsService);
    private readonly requestsService = inject(RequestsService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly router = inject(Router);
    private readonly location = inject(Location);
    private readonly destroyRef = inject(DestroyRef);
    private requestType: RequestType = RequestType.Payment;
    private rowVersion: string | undefined;
    private readForm: (() => DraftFormState) | undefined;
    private cleanSnapshot: string | undefined;

    /**
     * The technical Draft ID being edited, or null while the form holds a request that has not been saved as a draft.
     * It is what addresses and API calls use; users are shown {@link draftName}.
     */
    readonly draftId = signal<number | null>(null);

    /** The number of the draft being edited within its request type, or null if there is none. */
    readonly draftNumber = signal<number | null>(null);

    /** How the draft being edited is named to the user, such as "Payment draft 2", or null if there is none. */
    readonly draftName = computed(() => {
        const number = this.draftNumber();
        return number === null ? null : draftName(this.requestType, number);
    });

    /** The documents already saved on the draft. */
    readonly documents = signal<RequestDocAttachmentDto[]>([]);

    /** True while a save is in progress. */
    readonly saving = signal(false);

    /**
     * Says which request type the form creates. Call once, before anything else.
     * @param requestType The form's request type.
     */
    configure(requestType: RequestType): void {
        this.requestType = requestType;
    }

    /**
     * Tells the editor how to read the form, and starts warning the user, through the browser's own prompt,
     * if they close or reload the tab while the form holds unsaved changes. Call once, early.
     * Nothing counts as unsaved until {@link markClean} has recorded what the form looks like when it is clean.
     * @param read Returns the form's current snapshot and how many chosen documents are not yet uploaded.
     */
    watchForm(read: () => DraftFormState): void {
        this.readForm = read;

        const warnBeforeUnload = (event: BeforeUnloadEvent): void => {
            if (this.hasUnsavedChanges()) {
                event.preventDefault();
                event.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', warnBeforeUnload);
        this.destroyRef.onDestroy(() => window.removeEventListener('beforeunload', warnBeforeUnload));
    }

    /**
     * Records the form as it is now as "nothing to save". Call when the form has finished loading
     * (blank, or restored from a draft).
     */
    markClean(): void {
        if (this.readForm) {
            this.cleanSnapshot = serializeSnapshot(this.readForm().snapshot);
        }
    }

    /**
     * Whether the form holds anything that has not been saved: a change since it was last clean,
     * or documents chosen but not yet uploaded. Typing something and then undoing it is not a change.
     */
    hasUnsavedChanges(): boolean {
        if (!this.readForm || this.cleanSnapshot === undefined) return false;

        const current = this.readForm();
        return current.pendingFiles > 0 || serializeSnapshot(current.snapshot) !== this.cleanSnapshot;
    }

    /**
     * Opens a saved draft. On failure the user is told and taken back to the Drafts page.
     * @param draftId The Draft ID from the route.
     * @returns The saved form snapshot, or null if the draft could not be opened.
     */
    async open(draftId: number): Promise<DraftSnapshot | null> {
        try {
            const draft = await this.draftsService.get(draftId);
            if (draft.requestType !== this.requestType) {
                throw new Error(`Draft ${draftId} is a ${draft.requestType} draft, not a ${this.requestType} one.`);
            }

            this.draftId.set(draft.id);
            this.draftNumber.set(draft.draftNumber);
            this.rowVersion = draft.rowVersion;
            this.documents.set(draft.documents);

            const snapshot = parseSnapshot(draft.payload);
            if (!snapshot) {
                this.snackbarService.errorSnackBar('The saved details of this draft could not be read. You can start again and save over it.');
                return emptySnapshot();
            }
            return snapshot;
        } catch {
            this.snackbarService.errorSnackBar('Unable to open this draft. It may have been deleted or expired.');
            void this.router.navigate(['/draft-requests']);
            return null;
        }
    }

    /**
     * Saves the form as a draft: creates it the first time, updates it afterwards, then uploads any new documents.
     * The form can be incomplete or invalid.
     * @param input The form snapshot, its list summary and any new documents.
     */
    async save(input: DraftSaveInput): Promise<DraftSaveOutcome> {
        if (this.saving()) return DraftSaveOutcome.Failed;

        // A draft always belongs to a matter: its documents are filed under it, and the Drafts list shows it.
        if (!input.summary.matterNo) {
            this.snackbarService.errorSnackBar('Select a matter before saving as a draft.');
            return DraftSaveOutcome.Failed;
        }

        this.saving.set(true);
        try {
            let createdDraftId: number | null = null;
            const request: SaveDraftRequest = {
                requestType: this.requestType,
                payload: serializeSnapshot(input.snapshot),
                payloadVersion: DRAFT_PAYLOAD_VERSION,
                ...input.summary
            };

            const id = this.draftId();
            if (id === null) {
                const created = await this.draftsService.create(request);
                this.draftId.set(created.id);
                this.draftNumber.set(created.draftNumber);
                this.rowVersion = created.rowVersion;
                createdDraftId = created.id;
            } else {
                const updated = await this.draftsService.update(id, { ...request, rowVersion: this.rowVersion });
                this.rowVersion = updated.rowVersion;
            }
            // What was just saved is now the "nothing to save" state.
            this.cleanSnapshot = serializeSnapshot(input.snapshot);

            // Still "saving" while the documents upload: a submit now would consume the draft under the upload.
            const outcome = await this.uploadDocuments(input);
            if (createdDraftId !== null && outcome !== DraftSaveOutcome.Saved) {
                // The form stays open with the user's input, so only change the address; a refresh then reopens the draft.
                // After a complete save the caller returns to a blank form instead, and the address must stay as it is.
                this.location.replaceState(this.resumeUrl(createdDraftId));
            }
            return outcome;
        } catch (error) {
            this.snackbarService.errorSnackBar(this.describeSaveFailure(error));
            return DraftSaveOutcome.Failed;
        } finally {
            this.saving.set(false);
        }
    }

    /**
     * Removes a saved document from the draft.
     * @param documentId The document's id.
     */
    async removeDocument(documentId: number): Promise<void> {
        try {
            await this.requestsService.deleteRequestDocument(documentId);
            this.documents.update(documents => documents.filter(document => document.id !== documentId));
        } catch {
            this.snackbarService.errorSnackBar('Unable to remove the document. Please try again.');
        }
    }

    /**
     * Call after the form has been submitted as a request. A submitted draft no longer exists, so the form
     * returns to a blank create page; the draft counts are refreshed either way.
     */
    async afterSubmit(): Promise<void> {
        await this.returnToBlankForm();
    }

    /**
     * Call after the form has been completely saved as a draft (the draft and all its documents) and the form has been
     * reset. The draft now lives on the Drafts page, so the form returns to a blank create page, ready for the next
     * request, exactly as after a submit.
     */
    async afterDraftSaved(): Promise<void> {
        await this.returnToBlankForm();
    }

    private async returnToBlankForm(): Promise<void> {
        // Everything the form held is saved or submitted, so there is nothing to warn about (and leaving must not prompt).
        this.markClean();
        const wasDraft = this.draftId() !== null;
        this.draftId.set(null);
        this.draftNumber.set(null);
        this.documents.set([]);
        this.rowVersion = undefined;
        void this.draftsService.refreshCounts();

        if (wasDraft) {
            await this.router.navigate([draftTabFor(this.requestType).createPath], { replaceUrl: true });
        }
    }

    private async uploadDocuments(input: DraftSaveInput): Promise<DraftSaveOutcome> {
        const id = this.draftId();
        const name = this.draftName() ?? 'The draft';
        if (input.newFiles.length === 0 || id === null) {
            this.snackbarService.successSnackBar(`${name} saved.`);
            return DraftSaveOutcome.Saved;
        }

        try {
            await this.requestsService.addRequestDocument({
                requestId: 0,
                draftId: id,
                requestType: this.requestType,
                attachments: input.newFiles
            });
            const draft = await this.draftsService.get(id);
            this.documents.set(draft.documents);
            this.snackbarService.successSnackBar(`${name} saved.`);
            return DraftSaveOutcome.Saved;
        } catch {
            this.snackbarService.errorSnackBar(`${name} was saved, but its documents could not be uploaded. Please save again.`);
            return DraftSaveOutcome.DocumentsFailed;
        }
    }

    private resumeUrl(draftId: number): string {
        return `${draftTabFor(this.requestType).createPath}/draft/${draftId}`;
    }

    private describeSaveFailure(error: unknown): string {
        if (error instanceof HttpErrorResponse) {
            if (error.status === 409) return 'This draft was changed or removed elsewhere. Reload it and try again.';
            if (error.status === 404) return 'This draft no longer exists. It may have expired or been deleted.';
            if (error.status === 403) return 'You do not have permission to save drafts.';
        }
        return 'Unable to save the draft. Please try again.';
    }
}
